// src/components/Navbar.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Tooltip,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  useMediaQuery,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import Badge from "@mui/material/Badge";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArticleIcon from "@mui/icons-material/Article";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Person } from "@mui/icons-material";
import LanguageIcon from "@mui/icons-material/Language";

import navConfigB2B from "./NavConfigB2B";
import { getPrimaryColor } from "./GlobalFunction/Theme";
import DesktopMegaMenu from "./DesktopMegaMenu";
import { useAuth } from "./pages/Auth/AuthContext";
import { getLangFromCookie, setLangCookie } from "../Utils/LangCookie";
import { InquiryAPI } from "../api/Inquiry";
import { FavouriteAPI } from "../api/Favourite";
import ProtectedImage from "./GlobalFunction/ProtectedImage";
import { resolveCanonicalUserID } from "../Utils/UserIdentity";

const fonts = {
  EN: "LINE Seed Sans",
  ZH: "LINE Seed TW",
  JP: "LINE Seed JP",
};

/* ---------------- Mobile tree ---------------- */
function MobileTree({
  items,
  depth = 0,
  ancestors = [],
  onCloseDrawer,
  openPath,
  setOpenPath,
  isLoggedIn = false,
  onRequireLogin = () => {},
  ancestorLocked = false,
  modePrefix = "", // ✅ removed /b2b
  lang,
}) {
  const baseLabel = "商品資訊";
  const productInfoBase = "/商品資訊";

  const toForCategory = (href = "") => {
    if (!href) return productInfoBase;
    const search = `?cat=${encodeURIComponent(href)}`;
    return `${productInfoBase}${search}`;
  };

  return (
    <List component="div" disablePadding sx={{ display: "contents" }}>
      {items.map((item) => {
        const hasChildren = !!item.children?.length;
        const isExternal = !!item.external;

        const thisLocked =
          (!isLoggedIn && item.login === "required") || ancestorLocked;

        const ancestorsMatch = ancestors.every((lbl, i) => openPath[i] === lbl);
        const isOpen = ancestorsMatch && openPath[depth] === item.label;

        const toggleThis = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (thisLocked || !hasChildren) return;
          const nextPath = isOpen
            ? openPath.slice(0, depth)
            : [...ancestors, item.label];
          setOpenPath(nextPath);
        };

        const isQAPage =
          item.label?.includes("常見問題") || item.label?.includes("常見Q&A");

        const underProductTree =
          ancestors.includes(baseLabel) || ancestors[0] === baseLabel;

        const isB2BPhilosophy =
          (item.href &&
            (item.href === "/企業理念" || item.href === "企業理念")) ||
          item.label?.includes("企業理念");

        // ✅ 商品資訊 → 專業諮詢 behaves like mega menu category
        const isB2BConsulting =
          item.label?.includes("專業諮詢") ||
          item.label?.includes("Consultation") ||
          item.label?.includes("相談") ||
          item.href?.includes("專業諮詢") ||
          item.href?.includes("專人服務");

        const consultingHref = "/商品資訊/專業諮詢(專人服務)";

        const linkTo = thisLocked
          ? undefined
          : isExternal
            ? undefined
            : isB2BPhilosophy
              ? "/公司簡介"
              : isB2BConsulting
                ? toForCategory(consultingHref)
                : isQAPage
                  ? `/常見問題Q&A`
                  : underProductTree
                    ? toForCategory(item.href)
                    : item.href || "#";

        const hrefForExternal =
          !thisLocked && isExternal ? item.href : undefined;

        const isProductInfoParent =
          item.label?.includes("商品資訊") && hasChildren;

        return (
          <Box key={`${depth}-${item.label}`}>
            <ListItemButton
              component={thisLocked ? "div" : isExternal ? "a" : Link}
              to={!thisLocked && !isExternal ? linkTo : undefined}
              href={hrefForExternal}
              target={isExternal ? "_blank" : undefined}
              rel={isExternal ? "noopener noreferrer" : undefined}
              state={
                thisLocked || isExternal
                  ? undefined
                  : isB2BPhilosophy
                    ? { tab: 1 }
                    : isProductInfoParent
                      ? { freshEntry: true, _ts: Date.now() }
                      : (isB2BConsulting || underProductTree) &&
                          (isB2BConsulting ? consultingHref : item.href)
                        ? {
                            categoryHref: isB2BConsulting
                              ? consultingHref
                              : item.href,
                          }
                        : undefined
              }
              onClick={
                thisLocked
                  ? () => onRequireLogin(item.href)
                  : () => onCloseDrawer()
              }
              sx={{
                pl: 2 + depth * 2,
                pr: hasChildren ? 0.5 : 2,
                opacity: thisLocked ? 0.65 : 1,
                fontFamily: fonts[lang],
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.6,
                      fontSize: {
                        xs: "inherit",
                        sm: 20,
                      },

                      lineHeight: {
                        xs: "inherit",
                        sm: 1.5,
                      },
                    }}
                  >
                    <span>{item.label}</span>
                    {isExternal && !hasChildren && (
                      <OpenInNewRoundedIcon
                        sx={{ fontSize: 18, opacity: 0.9 }}
                      />
                    )}
                  </Box>
                }
              />

              {hasChildren && (
                <IconButton
                  size="small"
                  aria-label={isOpen ? "收合" : "展開"}
                  aria-expanded={isOpen ? "true" : "false"}
                  edge="end"
                  onClick={toggleThis}
                  sx={{ ml: "auto" }}
                >
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
            </ListItemButton>

            {hasChildren && (
              <Collapse in={!thisLocked && isOpen} timeout="auto" unmountOnExit>
                <MobileTree
                  items={item.children}
                  depth={depth + 1}
                  ancestors={[...ancestors, item.label]}
                  onCloseDrawer={onCloseDrawer}
                  openPath={openPath}
                  setOpenPath={setOpenPath}
                  isLoggedIn={isLoggedIn}
                  onRequireLogin={onRequireLogin}
                  ancestorLocked={thisLocked}
                  modePrefix={modePrefix}
                  lang={lang}
                />
              </Collapse>
            )}
          </Box>
        );
      })}
    </List>
  );
}

/* ---------------- Active underline helpers ---------------- */
const flattenHrefs = (node) => {
  const out = [];
  const walk = (n) => {
    if (!n) return;
    if (n.href) out.push(n.href);
    (n.children || []).forEach(walk);
  };
  walk(node);
  return out;
};

const norm = (p = "") =>
  String(p)
    .replace(/[?#].*$/, "")
    .replace(/\/+$/, "") || "/";

const startsWithRoute = (base, path) => {
  const b = norm(base);
  const p = norm(path);
  return p === b || p.startsWith(b + "/");
};

const useIsItemActive = () => {
  const { pathname, search, state } = useLocation();

  const safeDecode = (value = "") => {
    const text = String(value || "");

    try {
      return decodeURIComponent(text);
    } catch {
      return text;
    }
  };

  const normalizeRoute = (value = "") =>
    safeDecode(value)
      .replace(/[?#].*$/, "")
      .replace(/\/+$/, "") || "/";

  const routeStartsWith = (base = "", path = "") => {
    const normalizedBase = normalizeRoute(base);
    const normalizedPath = normalizeRoute(path);

    return (
      normalizedPath === normalizedBase ||
      normalizedPath.startsWith(`${normalizedBase}/`)
    );
  };

  const isExternalHref = (href = "") => {
    const raw = String(href || "").trim();
    return raw.startsWith("http://") || raw.startsWith("https://");
  };

  const asRoutePath = (href = "") => {
    const raw = String(href || "").trim();

    if (!raw || isExternalHref(raw)) {
      return "";
    }

    return raw.startsWith("/") ? raw : `/${raw}`;
  };

  const getActivePathsFromHref = (href = "") => {
    const raw = String(href || "").trim();
    const routePath = asRoutePath(raw);

    if (!routePath) {
      return [];
    }

    const normalizedRoutePath = normalizeRoute(routePath);
    const paths = [normalizedRoutePath];

    /*
     * The Vision & Philosophy menu opens /公司簡介 with tab state.
     * The parent navbar only needs to know that /公司簡介 belongs under SEHO.
     */
    if (normalizedRoutePath === "/企業理念") {
      paths.push("/公司簡介");
    }

    return Array.from(new Set(paths));
  };

  const isHrefActive = (href = "") => {
    const activePaths = getActivePathsFromHref(href);

    return activePaths.some((activePath) =>
      routeStartsWith(activePath, pathname),
    );
  };

  return (item) => {
    const allHrefs = [item.href, ...flattenHrefs(item)].filter(Boolean);

    const searchParams = new URLSearchParams(search);
    const rawCurrentCategory = searchParams.get("cat") || "";
    const currentCategory = rawCurrentCategory
      ? normalizeRoute(rawCurrentCategory)
      : "";

    const currentTab = Number(state?.tab ?? -1);

    /*
     * Normal route matching:
     * /公司簡介
     * /品牌資訊
     * /企業責任
     * /最新資訊/衛生資訊/:slug
     * /合作資訊/技術合作及聯合開發諮詢
     */
    if (allHrefs.some((href) => isHrefActive(href))) {
      return true;
    }

    /*
     * Product category matching:
     * /商品資訊?cat=/商品資訊/使用區域
     */
    if (
      currentCategory &&
      allHrefs.some((href) => {
        const normalizedHref = normalizeRoute(href);

        return (
          normalizedHref === currentCategory ||
          routeStartsWith(normalizedHref, currentCategory) ||
          routeStartsWith(currentCategory, normalizedHref)
        );
      })
    ) {
      return true;
    }

    /*
     * Product detail / product root fallback.
     */
    if (
      routeStartsWith("/商品資訊", pathname) &&
      allHrefs.some((href) => routeStartsWith("/商品資訊", href))
    ) {
      return true;
    }

    /*
     * /公司簡介 has two tabs. If the user entered it from 企業理念,
     * the route state contains tab: 1. Either way, it still belongs
     * to the SEHO parent menu.
     */
    if (
      normalizeRoute(pathname) === "/公司簡介" &&
      currentTab === 1 &&
      allHrefs.some((href) => normalizeRoute(href) === "/企業理念")
    ) {
      return true;
    }

    return false;
  };
};

/* ---------------- Language helpers ---------------- */
const normalizeLang = (code) => (code === "EN" || code === "JP" ? code : "ZH");

const getCurrentLang = () => normalizeLang(getLangFromCookie() || "ZH");

const LANGUAGE_BADGE_TEXT = {
  ZH: "中文",
  EN: "EN",
  JP: "日文",
};

const NAV_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      px: 1.4,
      py: 0.8,
      fontSize: {
        md: 12,
        lg: 14,
      },
      fontWeight: 400,
      lineHeight: 1.4,
    },
  },
};

const getTextByLang = (item, lang) => {
  if (!item) return "";
  if (lang === "EN") return item.EN || item.label || "";
  if (lang === "JP") return item.JP || item.label || "";
  return item.label || "";
};

const localizeNode = (node, lang) => {
  const out = { ...node, label: getTextByLang(node, lang) };
  if (Array.isArray(node.children))
    out.children = node.children.map((c) => localizeNode(c, lang));
  return out;
};
const localizeList = (items, lang) => items.map((n) => localizeNode(n, lang));

const ACCOUNT_MENU_TEXT = {
  profile: {
    ZH: "個人資料",
    EN: "Profile",
    JP: "プロフィール",
  },

  favourite: {
    ZH: "我的收藏",
    EN: "Favourites",
    JP: "お気に入り",
  },

  inquiryHistory: {
    ZH: "詢價歷史",
    EN: "Inquiry History",
    JP: "見積履歴",
  },

  retailHistory: {
    ZH: "報修紀錄",
    EN: "Repair History",
    JP: "修理履歴",
  },

  logout: {
    ZH: "登出",
    EN: "Logout",
    JP: "ログアウト",
  },
};

const getAccountMenuText = (key, lang) => {
  const l = lang === "EN" ? "EN" : lang === "JP" ? "JP" : "ZH";
  return ACCOUNT_MENU_TEXT[key]?.[l] || ACCOUNT_MENU_TEXT[key]?.ZH || "";
};

function LanguageMenu({ size = "medium", color = "inherit" }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [lang, setLang] = useState(getCurrentLang());

  useEffect(() => {
    const onLangChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onLangChange);
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("app:languageChanged", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);

  const setLanguage = (code) => {
    const L = normalizeLang(code);
    setLangCookie(L);
    setLang(L);
    setAnchorEl(null);
  };

  const languageOpt = { ZH: "語文", EN: "Language", JP: "言語" };
  // const languageOpt = { EN: "Language", JP: "言語" };
  return (
    <>
      <Tooltip
        title={languageOpt[lang]}
        arrow
        enterDelay={100}
        slotProps={NAV_TOOLTIP_SLOT_PROPS}
      >
        <Button
          size={size}
          color={color}
          aria-label={`Current language: ${LANGUAGE_BADGE_TEXT[lang]}`}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            minWidth: 0,
            px: 1.5,
            pr: 2,
            fontFamily: fonts[lang],
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 2,
            overflow: "visible",
          }}
        >
          <Badge
            badgeContent={LANGUAGE_BADGE_TEXT[lang]}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiBadge-badge": {
                minWidth: "auto",
                height: {
                  xs: 17,
                  sm: 19,
                },
                px: {
                  xs: 0.5,
                  sm: 0.65,
                },
                top: 1,
                right: -10,
                borderRadius: 10,
                // border: "2px solid #fffffc",
                // backgroundColor: getPrimaryColor("b2b"),
                color: getPrimaryColor("b2b"),
                fontFamily: fonts[lang],
                fontSize: {
                  xs: 12,
                  sm: 12,
                  md: 12,
                  lg: 12,
                },
                fontWeight: 400,
                lineHeight: 1,
                whiteSpace: "nowrap",
              },
            }}
          >
            <LanguageIcon />
          </Badge>
        </Button>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => setLanguage("ZH")}>中文</MenuItem>
        <MenuItem onClick={() => setLanguage("EN")}>English</MenuItem>
        <MenuItem onClick={() => setLanguage("JP")}>日本語</MenuItem>
      </Menu>
    </>
  );
}

/* ---------------- Main Navbar (B2B only, no /b2b prefix) ---------------- */
export default function NavbarB2B() {
  const mode = "b2b";
  const navbarPrimaryColor = getPrimaryColor(mode);
  const navbarDefaultColor = "text.secondary";

  const { user, logout } = useAuth();
  const isLoggedIn = !!user;

  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openPath, setOpenPath] = useState([]);

  // account dropdown
  const [accEl, setAccEl] = useState(null);
  const accOpen = Boolean(accEl);
  const openAccMenu = (e) => setAccEl(e.currentTarget);
  const closeAccMenu = () => setAccEl(null);

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setOpenPath([]);
    setAccEl(null);
  };

  // ✅ removed /b2b prefix
  const goProfile = () => {
    closeAccMenu();
    navigate(`/profile`);
  };

  const goFavourite = () => {
    closeAccMenu();
    navigate(`/我的收藏`);
  };

  const goInquiryHistory = () => {
    closeAccMenu();
    navigate(`/詢價/歷史`);
  };

  const goRetailHistory = () => {
    closeAccMenu();
    navigate(`/線上報修/紀錄`);
  };

  const doLogout = async () => {
    closeAccMenu();
    try {
      await logout();
    } finally {
      navigate(`/auth`, { replace: true });
    }
  };

  const handleCart = () => {
    const cartPath = `/詢價`;
    if (!isLoggedIn) {
      navigate(`/auth`, { state: { redirectTo: cartPath } });
    } else {
      navigate(cartPath);
    }
  };

  const handleFavourite = () => {
    const favPath = `/我的收藏`;
    if (!isLoggedIn) {
      navigate(`/auth`, { state: { redirectTo: favPath } });
    } else {
      navigate(favPath);
    }
  };

  const handleDrawerOpen = () => {
    if (!isMobile) {
      return;
    }

    setDrawerOpen(true);
  };

  /*
   * Close all mobile navigation UI as soon as the viewport changes
   * from mobile/tablet portrait to tablet landscape/desktop.
   */
  useEffect(() => {
    if (isMobile) {
      return;
    }

    setDrawerOpen(false);
    setOpenPath([]);
    setAccEl(null);
  }, [isMobile]);

  // Share the effective sidebar state with global fixed UI.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("app:sidebarStateChanged", {
        detail: {
          open: isMobile && drawerOpen,
        },
      })
    );
  }, [isMobile, drawerOpen]);

  // Open the sidebar remotely only while the mobile navbar is active.
  useEffect(() => {
    const toggleSidebar = (event) => {
      if (!isMobile) {
        setDrawerOpen(false);
        setOpenPath([]);
        setAccEl(null);
        return;
      }

      if (event.detail?.toggle) {
        setDrawerOpen((previousOpen) => !previousOpen);
      } else {
        setDrawerOpen(true);
      }
    };

    window.addEventListener("app:toggleSidebar", toggleSidebar);
    window.addEventListener("app:openSidebar", toggleSidebar);

    return () => {
      window.removeEventListener("app:toggleSidebar", toggleSidebar);
      window.removeEventListener("app:openSidebar", toggleSidebar);
    };
  }, [isMobile]);

  const isItemActive = useIsItemActive();

  // ---- language state (sync cookie + localStorage) ----
  const [lang, setLang] = useState(getCurrentLang());

  useEffect(() => {
    setLang(getCurrentLang());
  }, []);

  useEffect(() => {
    const onLangChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onLangChange);
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("app:languageChanged", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);

  // -------------------- Count badges (WordPress) --------------------
  const [cartCount, setCartCount] = useState(0);
  const [favCount, setFavCount] = useState(0);

  function resolveUserID(u) {
    return resolveCanonicalUserID(u);
  }

  async function loadCartCount() {
    try {
      if (!isLoggedIn) {
        setCartCount(0);
        return;
      }

      const uid = resolveUserID(user);
      if (!uid) {
        setCartCount(0);
        return;
      }

      const res = await InquiryAPI.getPendingCount(String(uid), { lang });
      const count = res?.count ?? res?.data?.count ?? 0;
      setCartCount(Number(count || 0));
    } catch (e) {
      setCartCount(0);
    }
  }

  async function loadFavCount() {
    try {
      if (!isLoggedIn) {
        setFavCount(0);
        return;
      }

      const uid = resolveUserID(user);
      if (!uid) {
        setFavCount(0);
        return;
      }

      const res = await FavouriteAPI.count(String(uid));
      const n = res?.count ?? res?.data?.count ?? res?.data ?? res ?? 0;
      setFavCount(Number(n || 0));
    } catch (e) {
      setFavCount(0);
    }
  }

  useEffect(() => {
    loadCartCount();
    loadFavCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user, location.pathname, lang]);

  useEffect(() => {
    const onUpdated = () => loadCartCount();
    window.addEventListener("inquiry:updated", onUpdated);
    return () => window.removeEventListener("inquiry:updated", onUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user, lang]);

  useEffect(() => {
    const onFavUpdated = () => loadFavCount();
    window.addEventListener("favourite:updated", onFavUpdated);
    return () => window.removeEventListener("favourite:updated", onFavUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user, lang]);
  // -------------------------------------------------------------------

  // localize nav items (B2B only)
  const rawItems = navConfigB2B;
  const navItemsLocalized = useMemo(
    () => localizeList(rawItems, lang),
    [rawItems, lang],
  );

  const loginOpt = { ZH: "登入", EN: "Login", JP: "ログイン" };
  const accountOpt = { ZH: "帳號", EN: "Account", JP: "アカウント" };
  const cartOpt = { ZH: "詢價表", EN: "Inquiry", JP: "見積リスト" };
  const favOpt = { ZH: "我的收藏", EN: "Favourites", JP: "お気に入り" };

  const navFontSize = { fontSize: { lg: 18, md: 16 } };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "#fffffc",
          color: navbarDefaultColor,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 64, md: 90 },
            py: { xs: 0.75, md: 1.5 },
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 1280,
              mx: "auto",
              px: { xs: 2, md: 3 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Link to="/首頁" style={{ display: "inline-flex" }}>
              <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                <ProtectedImage
                  src="/img/Logo/SEHO-pro.webp"
                  alt="SEHO pro"
                  ratio="5 / 1"
                  position="left center"
                  sx={{ width: { xs: 200, sm: 225, md: 240, lg: 280 } }}
                />
              </Box>
            </Link>

            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                {navItemsLocalized.map((item) => {
                  const label = item.label;
                  const isActive = isItemActive(item);
                  const isExternal = !!item.external;
                  const isProductTopMenu =
                    String(item.href || "") === "/商品資訊" ||
                    String(item.EN || "").toLowerCase() === "product" ||
                    String(item.label || "").includes("商品資訊");

                  if (isProductTopMenu) {
                    return (
                      <DesktopMegaMenu
                        key={label}
                        mode={mode}
                        lang={lang}
                        rootItem={{
                          ...item,
                          onClick: () =>
                            navigate("/商品資訊", {
                              replace: false,
                              state: { freshEntry: true, _ts: Date.now() },
                            }),
                        }}
                        primaryColor={getPrimaryColor(mode)}
                        active={isActive}
                        topAdjust={0}
                        defaultColor={navbarDefaultColor}
                        buttonSx={{
                          color: isActive
                            ? navbarPrimaryColor
                            : navbarDefaultColor,
                          bgcolor: "transparent",
                          ...navFontSize,
                          position: "relative",
                          transition: "color .18s ease",
                          "&:hover, &:focus-visible": {
                            bgcolor: "transparent",
                            color: navbarPrimaryColor,
                          },
                          "& .MuiButton-endIcon": { display: "none" },
                          "& svg[data-caret], & .menu-caret": {
                            display: "none !important",
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            left: 8,
                            right: 8,
                            bottom: 2,
                            height: 2,
                            backgroundColor: "currentColor",
                            transform: isActive ? "scaleX(1)" : "scaleX(0)",
                            transformOrigin: "center",
                            transition: "transform .18s ease",
                          },
                        }}
                      />
                    );
                  }

                  if (
                    label.includes("專業諮詢") ||
                    label.includes("Consultation") ||
                    label.includes("相談")
                  ) {
                    return (
                      <DesktopMegaMenu
                        key={label}
                        mode={mode}
                        lang={lang}
                        rootItem={{
                          ...item,
                          onClick: () =>
                            navigate("/商品資訊", {
                              state: {
                                categoryHref: "/商品資訊/專業諮詢（專人服務）",
                              },
                            }),
                        }}
                        primaryColor={getPrimaryColor(mode)}
                        active={isActive}
                        topAdjust={0}
                        defaultColor={navbarDefaultColor}
                        buttonSx={{
                          color: isActive
                            ? navbarPrimaryColor
                            : navbarDefaultColor,
                          bgcolor: "transparent",
                          ...navFontSize,
                          position: "relative",
                          transition: "color .18s ease",
                          "&:hover, &:focus-visible": {
                            bgcolor: "transparent",
                            color: navbarPrimaryColor,
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            left: 8,
                            right: 8,
                            bottom: 2,
                            height: 2,
                            backgroundColor: "currentColor",
                            transform: isActive ? "scaleX(1)" : "scaleX(0)",
                            transformOrigin: "center",
                            transition: "transform .18s ease",
                          },
                        }}
                      />
                    );
                  }

                  const isTopPhilosophy =
                    (item.href &&
                      (item.href === "/企業理念" ||
                        item.href === "企業理念")) ||
                    label.includes("企業理念");

                  if (!item.children) {
                    if (isExternal) {
                      return (
                        <Button
                          key={label}
                          component="a"
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={
                            <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
                          }
                          sx={{
                            color: isActive
                              ? navbarPrimaryColor
                              : navbarDefaultColor,
                            bgcolor: "transparent",
                            ...navFontSize,
                            position: "relative",
                            textTransform: "none",
                            transition: "color .18s ease",
                            "&:hover, &:focus-visible": {
                              bgcolor: "transparent",
                              color: navbarPrimaryColor,
                            },
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              left: 8,
                              right: 8,
                              bottom: 2,
                              height: 2,
                              backgroundColor: "currentColor",
                              transform: "scaleX(0)",
                              transformOrigin: "center",
                              transition: "transform .18s ease",
                            },
                          }}
                        >
                          {label}
                        </Button>
                      );
                    }

                    return (
                      <Button
                        key={label}
                        component={Link}
                        to={isTopPhilosophy ? "/公司簡介" : item.href || "#"}
                        state={isTopPhilosophy ? { tab: 1 } : undefined}
                        sx={{
                          color: isActive
                            ? navbarPrimaryColor
                            : navbarDefaultColor,
                          bgcolor: "transparent",
                          ...navFontSize,
                          position: "relative",
                          transition: "color .18s ease",
                          "&:hover, &:focus-visible": {
                            bgcolor: "transparent",
                            color: navbarPrimaryColor,
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            left: 8,
                            right: 8,
                            bottom: 2,
                            height: 2,
                            backgroundColor: "currentColor",
                            transform: isActive ? "scaleX(1)" : "scaleX(0)",
                            transformOrigin: "center",
                            transition: "transform .18s ease",
                          },
                        }}
                      >
                        {label}
                      </Button>
                    );
                  }

                  return (
                    <DesktopMegaMenu
                      key={label}
                      mode={mode}
                      lang={lang}
                      rootItem={item}
                      primaryColor={getPrimaryColor(mode)}
                      active={isActive}
                      topAdjust={0}
                      defaultColor={navbarDefaultColor}
                      buttonSx={{
                        color: isActive
                          ? navbarPrimaryColor
                          : navbarDefaultColor,
                        bgcolor: "transparent",
                        ...navFontSize,
                        transition: "color .18s ease",
                        "&:hover, &:focus-visible": {
                          bgcolor: "transparent",
                          color: navbarPrimaryColor,
                        },
                        "& .MuiButton-endIcon": { display: "none" },
                        "& svg[data-caret], & .menu-caret": {
                          display: "none !important",
                        },
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          left: 8,
                          right: 8,
                          bottom: 2,
                          height: 2,
                          backgroundColor: "currentColor",
                          transform: isActive ? "scaleX(1)" : "scaleX(0)",
                          transformOrigin: "center",
                          transition: "transform .18s ease",
                        },
                      }}
                    />
                  );
                })}

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LanguageMenu size="small" color="inherit" />

                  <Tooltip
                    title={isLoggedIn ? accountOpt[lang] : loginOpt[lang]}
                    arrow
                    enterDelay={100}
                    slotProps={NAV_TOOLTIP_SLOT_PROPS}
                  >
                    <IconButton
                      onClick={(e) =>
                        !isLoggedIn ? navigate(`/auth`) : openAccMenu(e)
                      }
                    >
                      <Person />
                    </IconButton>
                  </Tooltip>

                  {isLoggedIn && (
                    <Tooltip
                      title={favOpt[lang]}
                      arrow
                      enterDelay={200}
                      slotProps={NAV_TOOLTIP_SLOT_PROPS}
                    >
                      <IconButton onClick={handleFavourite}>
                        <Badge
                          badgeContent={favCount}
                          color="error"
                          overlap="circular"
                          sx={{
                            "& .MuiBadge-badge": {
                              fontWeight: 700,
                              minWidth: 18,
                              height: 18,
                              px: 0.5,
                              fontSize: 12,
                              border: "2px solid #fffffc",
                            },
                          }}
                          invisible={!favCount}
                        >
                          <FavoriteBorderRoundedIcon />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  )}

                  {isLoggedIn && (
                    <Tooltip
                      title={cartOpt[lang]}
                      arrow
                      enterDelay={100}
                      slotProps={NAV_TOOLTIP_SLOT_PROPS}
                    >
                      <IconButton onClick={handleCart}>
                        <Badge
                          badgeContent={cartCount}
                          color="error"
                          overlap="circular"
                          sx={{
                            "& .MuiBadge-badge": {
                              fontWeight: 700,
                              minWidth: 18,
                              height: 18,
                              px: 0.5,
                              fontSize: 12,
                              border: "2px solid #fffffc",
                            },
                          }}
                          invisible={!cartCount}
                        >
                          <ArticleIcon />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}

            {isMobile && (
              <IconButton color="inherit" onClick={handleDrawerOpen}>
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Menu anchorEl={accEl} open={accOpen} onClose={closeAccMenu}>
        <MenuItem onClick={goProfile}>
          {getAccountMenuText("profile", lang)}
        </MenuItem>
        {isLoggedIn && (
          <>
            <MenuItem onClick={goFavourite}>
              {getAccountMenuText("favourite", lang)}
            </MenuItem>
            <MenuItem onClick={goInquiryHistory}>
              {getAccountMenuText("inquiryHistory", lang)}
            </MenuItem>

            <MenuItem onClick={goRetailHistory}>
              {getAccountMenuText("retailHistory", lang)}
            </MenuItem>
          </>
        )}
        <Divider />
        <MenuItem onClick={doLogout}>
          {getAccountMenuText("logout", lang)}
        </MenuItem>
      </Menu>

      <Drawer
        anchor="right"
        open={isMobile && drawerOpen}
        onClose={handleDrawerClose}
      >
        <Box
          sx={{
            width: 320,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              bgcolor: "#fffffc",
              p: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "flex-end",
            }}
          >
            <LanguageMenu size="small" />

            <Tooltip
              title={isLoggedIn ? accountOpt[lang] : loginOpt[lang]}
              arrow
            >
              <IconButton
                aria-label="account"
                onClick={(e) => {
                  if (!isLoggedIn) {
                    navigate(`/auth`);
                    handleDrawerClose();
                  } else {
                    openAccMenu(e);
                  }
                }}
              >
                <Person />
              </IconButton>
            </Tooltip>

            {isLoggedIn && (
              <Tooltip
                title={favOpt[lang]}
                arrow
                slotProps={NAV_TOOLTIP_SLOT_PROPS}
              >
                <IconButton
                  aria-label="favourite"
                  onClick={() => {
                    handleFavourite();
                    handleDrawerClose();
                  }}
                >
                  <Badge
                    badgeContent={favCount}
                    color="error"
                    overlap="circular"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontWeight: 700,
                        minWidth: 18,
                        height: 18,
                        px: 0.5,
                        fontSize: 12,
                        border: "2px solid #fffffc",
                      },
                    }}
                    invisible={!favCount}
                  >
                    <FavoriteBorderRoundedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {isLoggedIn && (
              <IconButton
                aria-label="open cart"
                onClick={() => {
                  handleCart();
                  handleDrawerClose();
                }}
              >
                <Badge
                  badgeContent={cartCount}
                  color="error"
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontWeight: 700,
                      minWidth: 18,
                      height: 18,
                      px: 0.5,
                      fontSize: 12,
                      border: "2px solid #fffffc",
                    },
                  }}
                  invisible={!cartCount}
                >
                  <ArticleIcon />
                </Badge>
              </IconButton>
            )}
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <MobileTree
              items={navItemsLocalized}
              onCloseDrawer={handleDrawerClose}
              openPath={openPath}
              setOpenPath={setOpenPath}
              isLoggedIn={isLoggedIn}
              onRequireLogin={(href) =>
                navigate("/auth", { state: { redirectTo: href } })
              }
              modePrefix=""
              lang={lang}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
